<template>
  <section class="border-t border-default/60 bg-default">
    <UContainer class="py-12 sm:py-14">
      <div class="mx-auto max-w-md space-y-4">
        <h2 class="text-center text-xl font-semibold">
          {{ title || item?.name || 'ماشین‌حساب سلامت' }}
        </h2>
        <p
          v-if="subtitle"
          class="text-center text-sm text-muted"
        >
          {{ subtitle }}
        </p>
        <div
          v-if="!calculatorId || builderPreview"
          class="rounded-2xl border border-dashed p-6 text-center text-sm text-muted"
        >
          ماشین‌حساب سلامت
        </div>
        <LeadFlowShell
          v-else
          :labels="labels"
          :step="step"
          :loading="saving"
          :next-disabled="nextDisabled"
          :next-label="step === lastStep ? 'مشاهده نتیجه' : 'ادامه'"
          :show-next="!result"
          @next="next"
          @back="step = Math.max(0, step - 1)"
        >
          <LeadQuestionFields
            v-if="currentQuestions.length"
            v-model="answers"
            :questions="currentQuestions"
          />
          <template v-else-if="needsPhone">
            <AuthPhoneInput v-model="phone" />
          </template>
          <div
            v-else-if="result"
            class="space-y-2 text-center"
          >
            <p class="text-lg font-semibold">
              {{ result.title }}
            </p>
            <p class="text-sm text-muted">
              {{ result.description }}
            </p>
          </div>
        </LeadFlowShell>
        <p
          v-if="error"
          class="text-center text-sm text-error"
        >
          {{ error }}
        </p>
      </div>
    </UContainer>
  </section>
</template>

<script setup lang="ts">
import AuthPhoneInput from '~/components/auth/PhoneInput.vue'
import type { LeadQuestion } from '~/utils/leads/questions'
import { isCompleteIrMobile } from '~/utils/phone'
import { apiErrorMessage } from '~/utils/apiError'

const props = defineProps<{
  title?: string
  subtitle?: string
  calculatorId?: string
  builderPreview?: boolean
}>()

const item = ref<{ name: string; steps: LeadQuestion[]; requirePhoneForResult: boolean } | null>(null)
const answers = ref<Record<string, unknown>>({})
const phone = ref('')
const step = ref(0)
const saving = ref(false)
const error = ref('')
const result = ref<{ title: string; description: string } | null>(null)

if (import.meta.client && props.calculatorId && !props.builderPreview) {
  $fetch<{ item: NonNullable<typeof item.value> }>(`/api/leads/calculators/${props.calculatorId}`)
    .then((res) => { item.value = res.item })
    .catch(() => {})
}

const questions = computed(() => item.value?.steps ?? [])
const needsPhone = computed(() => Boolean(item.value?.requirePhoneForResult))
const lastStep = computed(() => questions.value.length + (needsPhone.value ? 0 : -1))
const labels = computed(() => [
  ...questions.value.map((_, i) => `سؤال ${i + 1}`),
  ...(needsPhone.value ? ['موبایل'] : []),
])
const currentQuestions = computed(() => {
  const q = questions.value[step.value]
  return q ? [q] : []
})
const nextDisabled = computed(() => {
  if (result.value) return true
  if (currentQuestions.value.length) {
    const q = currentQuestions.value[0]!
    return q.required !== false && (answers.value[q.id] == null || answers.value[q.id] === '')
  }
  return needsPhone.value && !isCompleteIrMobile(phone.value)
})

async function next() {
  if (step.value < questions.value.length - 1) {
    step.value += 1
    return
  }
  if (needsPhone.value && step.value === questions.value.length - 1) {
    step.value += 1
    return
  }
  if (!props.calculatorId) return
  saving.value = true
  error.value = ''
  try {
    const res = await $fetch<{ item: { result: { title: string; description: string } } }>(`/api/leads/calculators/${props.calculatorId}/submit`, {
      method: 'POST',
      body: { answers: answers.value, phone: phone.value || undefined },
    })
    result.value = res.item.result
    step.value = labels.value.length - 1
  }
  catch (err) {
    error.value = apiErrorMessage(err, 'محاسبه ناموفق بود')
  }
  finally {
    saving.value = false
  }
}
</script>
